import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGlobe, FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { languages, getLanguage } from '../i18n';
import { useTheme } from '../context/ThemeContext';

const LanguageSwitcher = () => {
  const { i18n, t } = useTranslation();
  const { isDarkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const currentCode = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];
  const currentLanguage = getLanguage(currentCode);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsOpen((open) => !open)}
        className={`flex items-center gap-2 px-3 py-2 rounded-full ${
          isDarkMode
            ? 'bg-gray-800 text-light hover:bg-gray-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } transition-all duration-200`}
        aria-label={t('nav.language')}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <FaGlobe className="w-4 h-4" />
        <span className="text-sm font-medium hidden sm:inline">{currentLanguage.nativeName}</span>
        <span className="text-sm sm:hidden">{currentLanguage.flag}</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.ul
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            role="listbox"
            aria-label={t('nav.language')}
            className={`absolute end-0 mt-2 w-48 max-h-80 overflow-y-auto rounded-xl py-2 shadow-xl z-50 ${
              isDarkMode
                ? 'bg-dark border border-primary/20'
                : 'bg-white border border-gray-200'
            }`}
          >
            {languages.map((language) => {
              const isActive = language.code === currentCode;

              return (
                <li key={language.code}>
                  <button
                    onClick={() => changeLanguage(language.code)}
                    role="option"
                    aria-selected={isActive}
                    lang={language.code}
                    dir={language.dir || 'ltr'}
                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-start transition-colors duration-200 ${
                      isActive
                        ? isDarkMode
                          ? 'text-primary bg-primary/10'
                          : 'text-green-600 bg-green-50'
                        : isDarkMode
                          ? 'text-light/80 hover:bg-gray-800 hover:text-primary'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-green-600'
                    }`}
                  >
                    <span className="text-base">{language.flag}</span>
                    <span className="flex-1">{language.nativeName}</span>
                    {isActive && <FaCheck className="w-3 h-3" />}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
